import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  Package,
  Building2,
  DollarSign,
  Users,
  Clock,
  MapPin,
  Plus,
  Eye
} from "lucide-react";
import { useService, useServiceOptions } from "@/hooks/useServices";
import type { ServiceOptionResponse } from "@/types/services";

interface BookingMarketplaceServiceDetailProps {
  bookingId: string;
  serviceId: string;
  onBack: () => void;
}

export default function BookingMarketplaceServiceDetail({
  bookingId,
  serviceId,
  onBack
}: BookingMarketplaceServiceDetailProps) {
  const [selectedOption, setSelectedOption] = useState<ServiceOptionResponse | null>(null);

  const { data: service, loading: serviceLoading } = useService(serviceId);
  const { data: serviceOptions = [], loading: optionsLoading } = useServiceOptions(serviceId);

  const formatPrice = (price?: number, currency: string = 'AED') => {
    if (!price) return "Custom Pricing";
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency
    }).format(price);
  };

  const getDurationText = (duration?: number) => {
    if (!duration) return "Custom Duration";
    if (duration < 60) return `${duration} minutes`;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours} hours`;
  };

  if (serviceLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Marketplace Browser
          </Button>
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Marketplace Browser
          </Button>
        </div>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Service not found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace Browser
        </Button>
      </div>

      {/* Service Details */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-xl">{service.name}</CardTitle>
              <div className="flex items-center gap-2 mt-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">{service.vendorName}</span>
                <span className="text-muted-foreground">•</span>
                <Badge variant="outline" className="text-xs">
                  {service.serviceCategoryName}
                </Badge>
                {service.serviceTypeName && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <Badge variant="outline" className="text-xs">
                      {service.serviceTypeName}
                    </Badge>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {service.description && (
            <div className="mb-6">
              <h3 className="font-medium mb-2">Description</h3>
              <p className="text-muted-foreground">{service.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground mb-1">Status</p>
              <Badge variant={service.isActive ? "default" : "secondary"}>
                {service.isActive ? "Active" : "Inactive"}
              </Badge>
            </div>
            {service.basePrice && (
              <div>
                <p className="text-muted-foreground mb-1">Starting Price</p>
                <p className="font-medium">{formatPrice(service.basePrice)}</p>
              </div>
            )}
            {service.maxCapacity && (
              <div>
                <p className="text-muted-foreground mb-1">Max Capacity</p>
                <p className="font-medium flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {service.maxCapacity} people
                </p>
              </div>
            )}
            {service.durationMinutes && (
              <div>
                <p className="text-muted-foreground mb-1">Duration</p>
                <p className="font-medium flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {getDurationText(service.durationMinutes)}
                </p>
              </div>
            )}
          </div>

          {service.location && (
            <div className="mt-4">
              <p className="text-muted-foreground mb-1">Location</p>
              <p className="text-sm flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {service.location}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Available Service Options
          </CardTitle>
        </CardHeader>
        <CardContent>
          {optionsLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : serviceOptions.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="font-medium text-foreground mb-2">No Options Available</h3>
              <p className="text-sm text-muted-foreground">
                This service doesn't have any configured options yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {serviceOptions.map((option, index) => (
                <div
                  key={option.id}
                  className="border rounded-lg p-4 hover:bg-accent/5 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium">
                        Option {index + 1}: {option.name}
                      </h4>
                      {option.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {option.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedOption(option)}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View Details
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3 text-muted-foreground" />
                      <span>{formatPrice(option.basePrice)}</span>
                    </div>
                    {option.durationMinutes && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span>{getDurationText(option.durationMinutes)}</span>
                      </div>
                    )}
                    {option.maxCapacity && (
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3 text-muted-foreground" />
                        <span>{option.maxCapacity} people</span>
                      </div>
                    )}
                    <div>
                      <Badge variant={option.isActive ? "default" : "secondary"} className="text-xs">
                        {option.isActive ? "Available" : "Inactive"}
                      </Badge>
                    </div>
                  </div>

                  {option.includedItems && option.includedItems.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-muted-foreground mb-2">Included:</p>
                      <div className="flex flex-wrap gap-1">
                        {option.includedItems.map((item: string, idx: number) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Option Detail Modal/Drawer would go here if needed */}
    </div>
  );
}